import React, { useCallback, useEffect, useState } from "react";
import { Check, Plus, Store, Trash2, X, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ActionIcon } from "../components/ActionIcon";
import VendorTemplates from "../components/VendorTemplates";

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/vendor-catalog");
    setVendors(data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const add = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/vendor-catalog", { name: newName });
      toast.success(`${newName} added to the vendor catalog`);
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const rename = async (vendor) => {
    try {
      await api.put(`/admin/vendor-catalog/${vendor.id}`, { name: editedName });
      toast.success("Vendor renamed");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (vendor) => {
    const warning = vendor.employers.length
      ? `Delete ${vendor.name}? It will also be removed from ${vendor.employers.length} employer list(s).`
      : `Delete ${vendor.name}?`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/admin/vendor-catalog/${vendor.id}`);
      toast.success("Vendor deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-vendors-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Vendors
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--muted)" }}>
          The master list of payment vendors. Assign them to employers from the Employers page — only
          assigned vendors appear on an employer's invoice form. Each employer assignment remains a
          separate vendor record with its own payment template.
        </p>
      </div>

      <form onSubmit={add} className="border p-6 mb-8 flex flex-col sm:flex-row gap-4 sm:items-end" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="add-catalog-vendor-form">
        <div className="flex-1">
          <label className="field-label">New vendor</label>
          <input className="input-flat" required minLength={2} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Northwind Medical Billing" data-testid="new-catalog-vendor-name"/>
        </div>
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="add-catalog-vendor-submit">
          <Plus size={14}/> {saving ? "Saving…" : "Add Vendor"}
        </button>
      </form>

      <div className="border max-h-[520px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="vendor-catalog-region">
        <table className="w-full text-sm min-w-[640px]" data-testid="vendor-catalog-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Vendor</th>
              <th className="px-4 py-3">Assigned employers</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor) => (
              <tr key={vendor.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`catalog-vendor-${vendor.id}`}>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>
                  {editingId === vendor.id ? (
                    <input className="input-flat" value={editedName} autoFocus onChange={(e) => setEditedName(e.target.value)} data-testid={`edit-catalog-vendor-input-${vendor.id}`}/>
                  ) : (
                    <span className="flex items-center gap-2"><Store size={14} style={{ color: "var(--muted)" }}/> {vendor.name}</span>
                  )}
                </td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>
                  {vendor.employers.length ? (
                    <span className="flex flex-wrap gap-1.5">
                      {vendor.employers.map((name) => <span key={name} className="pill text-[10px]">{name}</span>)}
                    </span>
                  ) : "Not assigned"}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1 justify-end">
                    {editingId === vendor.id ? (
                      <>
                        <ActionIcon icon={Check} label="Save name" onClick={() => rename(vendor)} testId={`save-catalog-vendor-${vendor.id}`}/>
                        <ActionIcon icon={X} label="Cancel" onClick={() => setEditingId(null)} testId={`cancel-catalog-vendor-${vendor.id}`}/>
                      </>
                    ) : (
                      <ActionIcon icon={Edit3} label="Rename vendor" onClick={() => { setEditingId(vendor.id); setEditedName(vendor.name); }} testId={`edit-catalog-vendor-${vendor.id}`}/>
                    )}
                    <ActionIcon icon={Trash2} label="Delete vendor" onClick={() => remove(vendor)} testId={`delete-catalog-vendor-${vendor.id}`}/>
                  </span>
                </td>
              </tr>
            ))}
            {!vendors.length && (
              <tr><td colSpan={3} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="vendor-catalog-empty">
                No vendors yet — add the first one above.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <VendorTemplates />
    </div>
  );
}
